import { languageName } from '@/nova/config/languageEndonyms';
import { toAppLang } from '@/nova/config/langCodeMap';

import type { AdSubject, AdSubjectType } from './banner-kit-api';

/**
 * The language an ad is written in. Every Marketing asset (Image Banners, HTML5 Banners, Video Ads, Mini-games,
 * Landing Pages) carries one, picked from the brand website's own languages and defaulting to the website's default.
 * All the words the ad shows are AD CONTENT in that language (the promotion's copy, the CTA, the legal line, fixed
 * per-template lines), never Studio UI copy, so none of them goes through `t()`. An empty language is an older
 * document: it renders exactly as before (English-first record copy, English tables).
 */

/** A site language id exactly as the website lists it (`en`, `mx`, `br`, `ar` (Argentina), `af` (Afrikaans), `cfr`,
 *  ...). '' = no language (an older document). */
export type AdLanguageId = string;

/** The languages the fixed per-language tables (CTA defaults, legal line, game lines, runtime labels) are written in. */
export type AdLanguageBase = 'en' | 'es' | 'pt' | 'fr' | 'af' | 'ro' | 'hu';

const BASE_BY_ID: Readonly<Record<string, AdLanguageBase>> = {
  en: 'en',
  cen: 'en',
  es: 'es',
  mx: 'es',
  cl: 'es',
  co: 'es',
  pe: 'es',
  ve: 'es',
  ar: 'es',
  arg: 'es',
  pt: 'pt',
  br: 'pt',
  bra: 'pt',
  fr: 'fr',
  cfr: 'fr',
  af: 'af',
  za: 'af',
  ro: 'ro',
  hu: 'hu',
};

const BASES: readonly AdLanguageBase[] = ['en', 'es', 'pt', 'fr', 'af', 'ro', 'hu'];
const isBase = (value: string): value is AdLanguageBase => (BASES as readonly string[]).includes(value);
/** The table's own row for an id (never an inherited member: an id like "constructor" has none). */
const baseRow = (key: string): AdLanguageBase | undefined =>
  Object.prototype.hasOwnProperty.call(BASE_BY_ID, key) ? BASE_BY_ID[key] : undefined;

/** Ids already reported as unknown (each is logged once, not on every render). */
const loggedUnknown = new Set<string>();

/** The base language of a site language id, for the fixed per-language tables: the platform's market ids map to
 *  their language (`mx` -> es, `br` -> pt, `cfr` -> fr, `za` -> af), a regional tag to its primary subtag (`es-MX` ->
 *  es), and anything else to English (logged once). '' is English without a log (an older document). Pure apart from
 *  that log. */
export const adLanguageBase = (id: AdLanguageId | undefined): AdLanguageBase => {
  const key = (id ?? '').trim().toLowerCase();
  if (!key) return 'en';
  const known = baseRow(key);
  if (known) return known;
  const primary = key.split(/[-_]/)[0];
  const viaPrimary = baseRow(primary) ?? (isBase(primary) ? primary : undefined);
  if (viaPrimary) return viaPrimary;
  if (!loggedUnknown.has(key)) {
    loggedUnknown.add(key);
    // eslint-disable-next-line no-console -- an ad language with no fixed-text row falls back to English: say so once.
    console.warn(`[ad-language] no fixed-text row for "${key}": using English`);
  }
  return 'en';
};

/** The language's own name for the picker (`mx` "Mexicano", `br` "Brasileiro", `ar` "Argentino", `af` "Afrikaans").
 *  An endonym: render it RAW, never through `t()`. Never `Intl` on the raw id (`br`/`ar` would read as Breton and
 *  Arabic): the id goes through the app's code map first. '' for no language. */
export const adLanguageLabel = (id: AdLanguageId | undefined): string => {
  const key = (id ?? '').trim();
  return key ? languageName(toAppLang(key)) : '';
};

/** The call to action an ad starts with when its subject carries none, per subject type and base language. AD
 *  CONTENT, not UI copy (never translated through i18n). The `en` row is `DEFAULT_AD_CTA` (ad-subjects.ts). */
export const DEFAULT_AD_CTA_BY_LANGUAGE: Readonly<Record<AdSubjectType, Readonly<Record<AdLanguageBase, string>>>> = {
  promotion: {
    en: 'CLAIM BONUS',
    es: 'RECLAMA TU BONO',
    pt: 'RESGATE O BÔNUS',
    fr: 'OBTENEZ LE BONUS',
    af: 'EIS JOU BONUS',
    ro: 'IA BONUSUL',
    hu: 'KÉRD A BÓNUSZT',
  },
  game: {
    en: 'PLAY NOW',
    es: 'JUEGA YA',
    pt: 'JOGUE AGORA',
    fr: 'JOUEZ MAINTENANT',
    af: 'SPEEL NOU',
    ro: 'JOACĂ ACUM',
    hu: 'JÁTSSZ MOST',
  },
  gamification: {
    en: 'JOIN NOW',
    es: 'ÚNETE YA',
    pt: 'PARTICIPE JÁ',
    fr: 'REJOIGNEZ-NOUS',
    af: 'SLUIT NOU AAN',
    ro: 'ALĂTURĂ-TE',
    hu: 'CSATLAKOZZ',
  },
};

/** The default call to action of a subject type in `language` (its base language's row); no language is today's
 *  English default. Pure. */
export const defaultAdCta = (type: AdSubjectType, language?: AdLanguageId): string =>
  DEFAULT_AD_CTA_BY_LANGUAGE[type][adLanguageBase(language)];

/** A field of the subject's copy that a localization can have translated. */
export type LocalizedCopyField = 'headline' | 'subtitle' | 'cta_text';

/** The subject's copy in one language, stored on an asset document as `localized_copy`: the website's own text in
 *  that language where it has some, else Lumio's faithful translation (`translated` names those fields). An empty
 *  `cta_text` means the subject has no call to action of its own (the per-language default applies). */
export type LocalizedCopy = {
  language: AdLanguageId;
  subject_id: string;
  headline: string;
  subtitle: string;
  cta_text: string;
  translated: LocalizedCopyField[];
};

/** Every field a localization carries, in order. */
export const LOCALIZED_FIELDS: readonly LocalizedCopyField[] = ['headline', 'subtitle', 'cta_text'];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
const str = (value: unknown): string => (typeof value === 'string' ? value : '');

/** A stored `localized_copy` read off the wire; undefined when there is none (or it names no language). Pure. */
export const normaliseLocalizedCopy = (raw: unknown): LocalizedCopy | undefined => {
  if (!isRecord(raw) || !str(raw.language)) return undefined;
  const translated = Array.isArray(raw.translated) ? raw.translated : [];
  return {
    language: str(raw.language),
    subject_id: str(raw.subject_id),
    headline: str(raw.headline),
    subtitle: str(raw.subtitle),
    cta_text: str(raw.cta_text),
    translated: LOCALIZED_FIELDS.filter((f) => translated.includes(f)),
  };
};

/** Whether `loc` is the copy of subject `subjectId` in `language`. Pure. */
const copyMatches = (loc: LocalizedCopy | undefined, language: string, subjectId: string): loc is LocalizedCopy =>
  loc !== undefined && loc.language === language && loc.subject_id === subjectId;

/**
 * The subject as an ad in `language` shows it. With no language, `subject` itself (today's behaviour). Otherwise its
 * headline, subtitle and CTA come from `loc` when `loc` is this subject's copy in this language (a blank localized
 * headline keeps the subject's own), and an empty CTA (the subject's own, or the localized one) becomes the subject
 * type's default in that language, whether or not `loc` has arrived. Never changes `id`, `name`, `image_url` or
 * `cta_href`: art-change detectors compare those. `subject` itself comes back when there is no language, or no
 * matching `loc` and a CTA of its own. Pure.
 */
export const localizedSubject = (
  subject: AdSubject,
  loc: LocalizedCopy | undefined,
  language: AdLanguageId | undefined,
  subjectType: AdSubjectType,
): AdSubject => {
  const lang = (language ?? '').trim();
  if (!lang) return subject;
  const own = copyMatches(loc, lang, subject.id)
    ? { ...subject, headline: loc.headline || subject.headline, subtitle: loc.subtitle, cta_text: loc.cta_text }
    : subject;
  if ((own.cta_text ?? '').trim()) return own;
  return { ...own, cta_text: defaultAdCta(subjectType, lang) };
};

/** Whether an ad in `language` has its subject's copy: always with no language (today's behaviour), else when `loc`
 *  is subject `subjectId`'s copy in that language. Pure. */
export const copyReadyIn = (
  loc: LocalizedCopy | undefined | null,
  language: AdLanguageId | undefined,
  subjectId: string,
): boolean => {
  const lang = (language ?? '').trim();
  return !lang || copyMatches(loc ?? undefined, lang, subjectId);
};
